import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorFallback } from "$/modules/shared/ui/error-fallback";

type ErrorBoundaryProps = {
	children: ReactNode;
};

type ErrorBoundaryState = {
	error: Error | null;
};

/**
 * Last-resort boundary wrapping the entire app. Catches render errors that
 * escape the router's per-route boundaries (e.g. failures inside the provider
 * tree) so the user sees a recoverable screen instead of a blank page.
 */
export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error("Uncaught error:", error, info.componentStack);
	}

	reset = () => {
		this.setState({ error: null });
	};

	render() {
		if (this.state.error) {
			return <ErrorFallback error={this.state.error} reset={this.reset} />;
		}

		return this.props.children;
	}
}
